import { useEffect, useRef } from 'react';

// The ONE Esc / focus implementation for every side drawer (DrawerFrame is the only caller).
// - Esc closes the TOPMOST active drawer; ignored when a nested modal / open popup owns the key.
// - Tab is trapped inside the panel; focus moves into the panel on open and returns to the opener on close.
const stack = [];
const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]):not([type=hidden]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';
const OWNS_ESC = '[aria-expanded="true"],[role="listbox"],[role="menu"]';

export default function useDrawerA11y(panelRef, onClose, active = true) {
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    const panel = panelRef.current;
    if (!active || !panel) return undefined;
    const opener = document.activeElement;
    const token = {};
    stack.push(token);
    if (!panel.contains(document.activeElement)) panel.focus({ preventScroll: true });
    const onKey = (e) => {
      if (stack[stack.length - 1] !== token || e.defaultPrevented) return;
      const el = e.target && e.target.closest ? e.target : panel;
      const modal = el.closest('[aria-modal="true"]');
      if (modal && modal !== panel && !panel.contains(modal)) return;   // a modal above this drawer owns the key
      if (e.key === 'Escape') {
        if (el.closest(OWNS_ESC)) return;
        if (close.current) close.current();
      } else if (e.key === 'Tab') {
        const f = [...panel.querySelectorAll(FOCUSABLE)].filter(n => !n.hidden);
        if (!f.length) { e.preventDefault(); return; }
        const first = f[0], last = f[f.length - 1];
        if (e.shiftKey && (document.activeElement === first || document.activeElement === panel)) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      const i = stack.indexOf(token);
      if (i >= 0) stack.splice(i, 1);
      if (opener && opener !== document.body && opener.isConnected && opener.focus) opener.focus({ preventScroll: true });
    };
  }, [active, panelRef]);
}
